import React, { FC, useState } from 'react'
import { Box, CircularProgress, Tooltip } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { SoapImprovementSuggestion } from '@/apps/latest/features/formatter/types/soapImprovement'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'
import { RADIUS } from '@/shared/styles/radii'

interface SoapImprovementCardProps {
  suggestion: SoapImprovementSuggestion
  onExecute?: (suggestion: SoapImprovementSuggestion) => Promise<void>
}

const STATUS_TAG: Record<SoapImprovementSuggestion['status'], string | null> = {
  pending: null,
  applied: '実行済',
  dismissed: '却下済',
}

// Notion 流の細い 1 行. 行のどこを押しても反映する (= 「実行」 文言や i アイコンは置かず、
// hover で淡い地 + tooltip 「✨ 反映」 で押下可能と示す. 右端に常設アイコンは出さない).
// reason (= suggestion.explanation) は latest では出さない (= legacy 専用. 詳細は server 側
// SoapImprovementSuggestion.explanation の field comment 参照).
export const SoapImprovementCard: FC<SoapImprovementCardProps> = ({
  suggestion,
  onExecute,
}) => {
  const [isExecuting, setIsExecuting] = useState(false)
  const isPending = suggestion.status === 'pending'
  const isDismissed = suggestion.status === 'dismissed'
  const tag = STATUS_TAG[suggestion.status]

  const handleExecute = async () => {
    if (isExecuting || !isPending) return
    setIsExecuting(true)
    try {
      await onExecute?.(suggestion)
    } finally {
      setIsExecuting(false)
    }
  }

  // hover tooltip = ✨ + 「クリックで反映」. 右端に常設アイコンは置かず、 反映可能であることは
  // この tooltip 文言だけで示す.
  const hoverTitle = isPending ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <AutoAwesomeIcon sx={{ fontSize: 12 }} />
      反映
    </Box>
  ) : (
    ''
  )

  return (
    <Tooltip title={hoverTitle} placement="left">
      <Box
        onClick={isPending ? handleExecute : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0.5,
          py: 0.25,
          borderRadius: `${RADIUS.sm}px`,
          cursor: isPending ? 'pointer' : 'default',
          opacity: isDismissed ? 0.5 : 1,
          '&:hover': isPending
            ? { bgcolor: colorTokens.overlay.hover }
            : undefined,
        }}
      >
        <BodyText sx={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap' }}>
          ・{suggestion.label}
        </BodyText>
        {tag && (
          <Box
            component="span"
            sx={{
              px: 0.75,
              fontSize: 11,
              fontWeight: 500,
              color: colorTokens.fg.muted,
              border: `1px solid ${colorTokens.border.default}`,
              borderRadius: `${RADIUS.sm}px`,
              flexShrink: 0,
            }}
          >
            {tag}
          </Box>
        )}
        {/* 反映処理中だけ右端に控えめな spinner (= 常設アイコンではない). */}
        {isExecuting && (
          <CircularProgress
            size={12}
            thickness={3}
            sx={{ color: colorTokens.fg.muted, flexShrink: 0 }}
          />
        )}
      </Box>
    </Tooltip>
  )
}
